import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Users, Archive, FileText, Video, CheckCircle, XCircle, AlertCircle, TrendingUp, ThumbsUp, ThumbsDown, ArrowRight, ChevronLeft, Building, Lightbulb, Target } from 'lucide-react';
import Card from './ui/Card';
import Input from './ui/Input';
import Select from './ui/Select';
import Button from './ui/Button';
import ListItem from './ui/ListItem';
import useStore from '../store/useStore';
import { filterAccounts } from '../utils/filterAccounts';

type ArchiveType = 'accounts' | 'competitors';

function WinLossArchive() {
  const { accounts, filters, setFilters, searchTerm, setSearchTerm } = useStore();
  const [archiveType, setArchiveType] = useState<ArchiveType>('accounts');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const filteredAccounts = useMemo(() => filterAccounts(accounts, filters, searchTerm), [accounts, filters, searchTerm]);

  const competitors = useMemo(() => {
    const uniqueCompetitors = [...new Set(accounts.map(account => account.competitor))];
    return uniqueCompetitors.map(competitor => ({
      name: competitor,
      deals: accounts.filter(account => account.competitor === competitor)
    }));
  }, [accounts]);

  useEffect(() => {
    if (archiveType === 'accounts' && filteredAccounts.length > 0 && !selectedItem) {
      setSelectedItem(filteredAccounts[0]);
    } else if (archiveType === 'competitors' && competitors.length > 0 && !selectedItem) {
      setSelectedItem(competitors[0]);
    }
  }, [filteredAccounts, competitors, selectedItem, archiveType]);

  const uniqueValues = useMemo(() => {
    return filteredAccounts.reduce((acc, item) => {
      ['competitor', 'industry', 'dealStage'].forEach(key => {
        if (!acc[key]) acc[key] = new Set();
        acc[key].add(item[key]);
      });
      return acc;
    }, {});
  }, [filteredAccounts]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setShowDetails(true);
  };

  const renderCompetitorDetails = (competitor) => {
    const deals = accounts.filter(account => account.competitor === competitor.name);
    const strengths = new Set();
    const weaknesses = new Set();
    const contexts = new Set();

    deals.forEach(deal => {
      if (deal.dealStage === 'Closed Won') {
        if (deal.whyChosen) strengths.add(deal.whyChosen);
        if (deal.concerns) weaknesses.add(deal.concerns);
      } else if (deal.dealStage === 'Closed Lost') {
        if (deal.perceivedStrengths) strengths.add(deal.perceivedStrengths);
        if (deal.whyNotChosen) weaknesses.add(deal.whyNotChosen);
      }
      if (deal.background) contexts.add(deal.background);
    });

    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-blue-700 mb-4">Competitor Analysis: {competitor.name}</h3>
          
          <DetailSection
            icon={<Building className="text-blue-500" size={20} />}
            title="Context"
            content={Array.from(contexts).join('\n\n')}
          />

          <DetailSection
            icon={<ThumbsUp className="text-green-500" size={20} />}
            title="Strengths"
            content={Array.from(strengths).join('\n\n')}
          />

          <DetailSection
            icon={<ThumbsDown className="text-red-500" size={20} />}
            title="Weaknesses"
            content={Array.from(weaknesses).join('\n\n')}
          />

          <DetailSection
            icon={<Target className="text-purple-500" size={20} />}
            title="Actionable Takeaways for Sales Teams"
            content={`
              1. Focus on our unique differentiators that address their weaknesses
              2. Prepare responses to their known strengths
              3. Leverage our success stories in areas where we outperform them
              4. Build strong relationships early in the sales cycle
              5. Emphasize our specific industry expertise and compliance capabilities
            `}
          />
        </div>

        <div>
          <h4 className="text-lg font-semibold text-blue-700 mb-4">Related Deals</h4>
          <div className="space-y-4">
            {deals.map((deal, index) => (
              <div key={index} className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-blue-800">{deal.industry}</span>
                  <span className={`text-sm px-2 py-1 rounded ${
                    deal.dealStage === 'Closed Won' 
                      ? 'bg-green-100 text-green-700' 
                      : deal.dealStage === 'Closed Lost'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {deal.dealStage}
                  </span>
                </div>
                <p className="text-sm text-blue-600 mb-2">{deal.background}</p>
                <div className="flex gap-2">
                  <a href="#" className="text-xs flex items-center text-blue-600 hover:text-blue-800">
                    <FileText size={14} className="mr-1" />
                    Transcript
                  </a>
                  <a href="#" className="text-xs flex items-center text-blue-600 hover:text-blue-800">
                    <Video size={14} className="mr-1" />
                    Interview
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderAccountDetails = (account) => {
    const isClosedWon = account.dealStage === 'Closed Won';
    const isClosedLost = account.dealStage === 'Closed Lost';

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-xl font-semibold text-blue-700 mb-4">{account.competitor}</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm text-blue-600 font-semibold">Industry</p>
              <p className="text-blue-800">{account.industry}</p>
            </div>
            <div>
              <p className="text-sm text-blue-600 font-semibold">Deal Stage</p>
              <p className={`inline-flex items-center ${
                isClosedWon ? 'text-green-600' : isClosedLost ? 'text-red-600' : 'text-blue-600'
              }`}>
                {isClosedWon && <CheckCircle size={16} className="mr-1" />}
                {isClosedLost && <XCircle size={16} className="mr-1" />}
                {account.dealStage}
              </p>
            </div>
          </div>
          <div className="flex gap-4 mb-6">
            <a href="#" className="flex items-center text-blue-600 hover:text-blue-800 text-sm">
              <FileText size={16} className="mr-1" />
              Win/Loss Transcript
            </a>
            <a href="#" className="flex items-center text-blue-600 hover:text-blue-800 text-sm">
              <Video size={16} className="mr-1" />
              Win/Loss Interview
            </a>
          </div>
        </div>

        <DetailSection
          icon={<Building className="text-blue-500" size={20} />}
          title="Customer Background & Pain Points"
          content={account.background}
        />

        {isClosedWon ? (
          <>
            <DetailSection
              icon={<CheckCircle className="text-green-500" size={20} />}
              title="Why We Were Chosen"
              content={account.whyChosen}
            />
            <DetailSection
              icon={<AlertCircle className="text-yellow-500" size={20} />}
              title="Concerns or Risks in the Deal"
              content={account.concerns}
            />
            <DetailSection
              icon={<TrendingUp className="text-blue-500" size={20} />}
              title="Initial Wins & Next Steps"
              content={account.nextSteps}
            />
          </>
        ) : isClosedLost && (
          <>
            <DetailSection
              icon={<ThumbsUp className="text-green-500" size={20} />}
              title="Our Perceived Strengths"
              content={account.perceivedStrengths}
            />
            <DetailSection
              icon={<ThumbsDown className="text-red-500" size={20} />}
              title="Why We Were Not Chosen"
              content={account.whyNotChosen}
            />
            <DetailSection
              icon={<ArrowRight className="text-blue-500" size={20} />}
              title="Customer's Decision & Next Steps"
              content={account.customerDecision}
            />
            <DetailSection
              icon={<Lightbulb className="text-yellow-500" size={20} />}
              title="Key Insights for Improvement"
              content={account.keyInsights}
            />
          </>
        )}
      </div>
    );
  };

  const renderItemDetails = () => {
    if (!selectedItem) return null;

    if (archiveType === 'competitors') {
      return renderCompetitorDetails(selectedItem);
    }

    return renderAccountDetails(selectedItem);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-6 sm:mb-8 text-center">Win/Loss Archive</h1>
      
      <div className="mb-6 sm:mb-8">
        <div className="flex border-b border-blue-200 overflow-x-auto">
          <button
            onClick={() => {
              setArchiveType('accounts');
              setSelectedItem(null);
            }}
            className={`flex items-center px-3 sm:px-4 py-2 font-semibold text-sm focus:outline-none whitespace-nowrap ${
              archiveType === 'accounts'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-blue-400 hover:text-blue-600'
            }`}
          >
            <Archive className="mr-2" size={18} />
            Accounts
          </button>
          <button
            onClick={() => {
              setArchiveType('competitors');
              setSelectedItem(null);
            }}
            className={`flex items-center px-3 sm:px-4 py-2 font-semibold text-sm focus:outline-none whitespace-nowrap ${
              archiveType === 'competitors'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-blue-400 hover:text-blue-600'
            }`}
          >
            <Users className="mr-2" size={18} />
            Competitors
          </button>
        </div>
      </div>

      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row gap-4">
        <div className="relative w-full sm:w-64">
          <Input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400" size={20} />
        </div>
        {archiveType === 'accounts' && ['competitor', 'industry', 'dealStage'].map((filterType) => (
          <Select
            key={filterType}
            value={filters[filterType]}
            onChange={(e) => handleFilterChange(filterType, e.target.value)}
            options={[
              { value: '', label: `All ${filterType.charAt(0).toUpperCase() + filterType.slice(1)}s` },
              ...Array.from(uniqueValues[filterType] || []).map(value => ({ value, label: value }))
            ]}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title={archiveType === 'accounts' ? 'Accounts' : 'Competitors'} className={`lg:col-span-1 ${showDetails ? 'hidden lg:block' : ''}`}>
          <ul className="space-y-2 overflow-y-auto max-h-[calc(100vh-300px)]">
            <AnimatePresence>
              {(archiveType === 'accounts' ? filteredAccounts : competitors).map((item) => (
                <motion.div
                  key={archiveType === 'accounts' ? item.id : item.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <ListItem
                    title={archiveType === 'accounts' ? `${item.competitor} - ${item.industry}` : item.name}
                    subtitle={archiveType === 'accounts' 
                      ? item.dealStage 
                      : `${item.deals.length} Deals`
                    }
                    onClick={() => handleItemSelect(item)}
                    isActive={selectedItem && (
                      archiveType === 'accounts' 
                        ? selectedItem.id === item.id
                        : selectedItem.name === item.name
                    )}
                    icon={archiveType === 'accounts' ? (
                      item.dealStage === 'Closed Won' 
                        ? <CheckCircle className="text-green-500" size={16} /> 
                        : item.dealStage === 'Closed Lost'
                        ? <XCircle className="text-red-500" size={16} />
                        : <AlertCircle className="text-yellow-500" size={16} />
                    ) : (
                      <Building className="text-blue-500" size={16} />
                    )}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </ul>
        </Card>

        <div className={`lg:col-span-2 ${showDetails ? '' : 'hidden lg:block'}`}>
          <Card title={archiveType === 'accounts' ? 'Account Details' : 'Competitor Details'}>
            {showDetails && (
              <Button
                onClick={() => setShowDetails(false)}
                className="mb-4 lg:hidden"
                variant="outline"
              >
                <ChevronLeft size={16} className="mr-2" />
                Back to List
              </Button>
            )}
            <AnimatePresence mode="wait">
              {selectedItem ? (
                <motion.div
                  key={archiveType === 'accounts' ? selectedItem.id : selectedItem.name}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderItemDetails()}
                </motion.div>
              ) : (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-blue-600"
                >
                  Select an item to view details
                </motion.p>
              )}
            </AnimatePresence>
          </Card>
        </div>
      </div>
    </div>
  );
}

const DetailSection = ({ icon, title, content }) => (
  <div className="bg-blue-50 p-4 rounded-lg mb-4">
    <div className="flex items-center mb-2">
      {icon}
      <h4 className="text-blue-800 font-semibold ml-2">{title}</h4>
    </div>
    <p className="text-blue-700 text-sm whitespace-pre-line">{content}</p>
  </div>
);

export default WinLossArchive;